import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// WICHTIG: gemeinsame Axios-Instanzen benutzen (Cookies, CSRF, Preflights)
import { surveyApi, mentoringApi as mApi } from '../../axiosInstance';

function MentoringInviteTile({ selectedSurveyId, tenantId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [surveyTitle, setSurveyTitle] = useState('');
  const [surveyMenteeCount, setSurveyMenteeCount] = useState(0);
  const [surveyMentorCount, setSurveyMentorCount] = useState(0);
  const [surveyNoneCount, setSurveyNoneCount] = useState(0);

  const [invitationsSent, setInvitationsSent] = useState(0);
  const [registeredMentees, setRegisteredMentees] = useState(0);
  const [registeredMentors, setRegisteredMentors] = useState(0);
  const [registeredLeaders, setRegisteredLeaders] = useState(0);
  const [error, setError] = useState('');

  // ---- Helper: Survey-Session sicherstellen, bevor wir /survey aufrufen ----
  async function ensureSurveySessionOnce() {
    try {
      const who = await fetch('/survey/whoami', { credentials: 'include' }).then((r) =>
        r.ok ? r.json() : null
      );
      if (who?.hasUser) return;
    } catch {}

    try {
      const tp = await fetch('/mentoring/tenant-package', { credentials: 'include' }).then((r) =>
        r.ok ? r.json() : null
      );
      if (tp?.email && tp?.customer_id) {
        await fetch('/survey/sso-attach', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user: {
              email: tp.email,
              role: 'hr',
              customer_id: tp.customer_id,
              client_id: tp.customer_id,
            },
          }),
        });
      }
    } catch {}
  }

  // ---- Loader ----
  useEffect(() => {
    let alive = true;
    if (!tenantId || !selectedSurveyId) return;

    (async () => {
      setError('');

      // 1) Titel aus Survey-Liste (über customer_id filtern)
      try {
        await ensureSurveySessionOnce();
        const { data } = await surveyApi.get('/list', {
          params: { customer_id: tenantId },
          withCredentials: true,
          validateStatus: (s) => s >= 200 && s < 300,
        });

        const arr = Array.isArray(data) ? data : [];
        const found = arr.find((s) => Number(s.survey_id) === Number(selectedSurveyId));

        if (alive) {
          setSurveyTitle(found?.title || '');
          // (falls vorhanden) grobe Werte aus der Survey-Liste setzen
          setSurveyMenteeCount(Number(found?.interests?.mentees_total ?? 0));
          setSurveyMentorCount(Number(found?.interests?.mentors_total ?? 0));
          setSurveyNoneCount(Number(found?.interests?.none_total ?? 0));
        }
      } catch {
        if (alive) setSurveyTitle('');
      }

      // 1b) Interessen-Counts (präziser) – primär Mentoring-Backend, fallback Survey-Results
      try {
        const { data } = await mApi.get('/hr/overview/interest', {
          params: { survey_id: selectedSurveyId },
          withCredentials: true,
          validateStatus: (s) => s >= 200 && s < 300,
        });

        if (alive) {
          setSurveyMenteeCount(Number(data?.mentees ?? 0));
          setSurveyMentorCount(Number(data?.mentors ?? 0));
          // optional: je nach Backend-Shape
          setSurveyNoneCount(Number(data?.none ?? data?.none_count ?? 0));
        }
      } catch (e) {
        // Fallback: Survey-Service Results-Route (WICHTIG: richtige Route!)
        try {
          await ensureSurveySessionOnce();
          const r = await fetch(`/survey/surveys/${selectedSurveyId}/results`, {
            credentials: 'include',
          });

          if (!r.ok) {
            const body = await r.text().catch(() => '');
            // bewusst warnen (wie in deiner Konsole sichtbar)
            console.warn(
              '[MentoringInviteTile] /survey results not ok:',
              r.status,
              body?.slice?.(0, 500) || body
            );
          } else {
            const d = await r.json();
            if (alive) {
              setSurveyMenteeCount(Number(d?.mentee_count ?? 0));
              setSurveyMentorCount(Number(d?.mentor_count ?? 0));
              setSurveyNoneCount(Number(d?.none_count ?? 0));
            }
          }
        } catch {
          // Wenn auch das nicht geht, bleiben die bestehenden Werte stehen.
        }
      }

      // 2) Einladungen gesamt (Mentoring)
      try {
        const { data } = await mApi.get('/invitations/stats', {
          params: { tenant_id: tenantId, survey_id: selectedSurveyId },
          withCredentials: true,
          validateStatus: (s) => s >= 200 && s < 300,
        });
        if (alive) {
          const invitesAll = Number(data?.invites_mentee ?? 0) + Number(data?.invites_mentor ?? 0);
          setInvitationsSent(invitesAll);
        }
      } catch {
        if (alive) setInvitationsSent(0);
      }

      // 3) Registrierte Nutzer (Mentoring)
      try {
        const { data } = await mApi.get(`/analysis/survey/${selectedSurveyId}`, {
          withCredentials: true,
          validateStatus: (s) => s >= 200 && s < 300,
        });
        if (alive) {
          setRegisteredMentees(Number(data?.registered_mentee ?? data?.mentees_total ?? 0));
          setRegisteredMentors(Number(data?.registered_mentor ?? data?.mentors_total ?? 0));
          setRegisteredLeaders(Number(data?.registered_leader ?? data?.leaders_total ?? 0));
        }
      } catch {
        if (alive) {
          setRegisteredMentees(0);
          setRegisteredMentors(0);
          setRegisteredLeaders(0);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [tenantId, selectedSurveyId]);

  const handleGoToInvite = () => {
    navigate(`/hr/invitations?survey_id=${selectedSurveyId}`);
  };

  return (
    <div className="bg-gray-800 p-6 rounded-2xl shadow-md">
      <h3 className="text-xl font-semibold mb-4">{t('mentoring', 'Mentoring')}</h3>

      {/* Hinweis nur noch, wenn wirklich gar nichts geladen werden konnte */}
      {error && <p className="text-red-400 mb-4">{error}</p>}

      <div className="space-y-2 text-gray-200 mb-6">
        <p className="text-sm">
          <span className="text-gray-400">
            {t('project', 'Projekt')} (ID {selectedSurveyId}):
          </span>
          <br />
          {surveyTitle || '—'}
        </p>

        <p className="text-sm">
          <span className="text-gray-400">{t('interestFromSurvey', 'Interesse aus Umfrage')}:</span>
          <br />
          {t('menteesTotal', 'Mentees (gesamt)')}: {surveyMenteeCount} |{' '}
          {t('mentorsTotal', 'Mentoren (gesamt)')}: {surveyMentorCount}
          {' | '}
          {t('noneTotal', 'Gar nicht (gesamt)')}: {surveyNoneCount}
        </p>

        <p className="text-sm">
          <span className="text-gray-400">{t('invitationsSent', 'Einladungen gesendet')}:</span>
          <br />
          {invitationsSent}
        </p>

        <p className="text-sm">
          <span className="text-gray-400">{t('registeredUsers', 'Registrierte Nutzer')}:</span>
          <br />
          {t('menteesTotal', 'Mentees (gesamt)')}: {registeredMentees} |{' '}
          {t('mentorsTotal', 'Mentoren (gesamt)')}: {registeredMentors} |{' '}
          {t('leadersTotal', 'Führungskräfte')}: {registeredLeaders}
        </p>
      </div>

      <button
        onClick={handleGoToInvite}
        className="bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 px-6 rounded-xl transition w-full"
      >
        {t('createInvitations', 'Einladungen erstellen')}
      </button>
    </div>
  );
}

export default MentoringInviteTile;
